import { getUserOrdersQuery } from '@/api/orders/orderQueries';
import type { UsersFilters } from '@/api/users/usersQueries';
import { getUsersQuery } from '@/api/users/usersQueries';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { User } from '@/domain/User';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { ModifyUserCart } from './ModifyUserCart';
import { ShowPatientsFilters } from './ShowPatientsFilter';

const INITIAL_SHOW_PATIENTS_FILTERS: UsersFilters = { role: 'Customer' };

export default function ShowPatients({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<UsersFilters>(
    INITIAL_SHOW_PATIENTS_FILTERS,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [fetchingUserId, setFetchingUserId] = useState<number | null>(null);
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_SHOW_PATIENTS_FILTERS);
    setPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);

  const { data: response, isLoading } = useQuery({
    ...getUsersQuery(page, filters),
  });

  const users = (response?.data || []) as Array<User>;
  const meta = response?.meta;

  const { isFetching: isOrdersFetching, isSuccess: isOrdersSuccess } = useQuery(
    {
      ...getUserOrdersQuery(fetchingUserId ?? 0),
      enabled: !!fetchingUserId,
    },
  );

  useEffect(() => {
    if (isOrdersSuccess && !isOrdersFetching && fetchingUserId) {
      const user = users.find((u) => u.id === fetchingUserId);
      if (user) {
        setSelectedUser(user);
        setDialogOpen(true);
      }
      setFetchingUserId(null);
    }
  }, [isOrdersSuccess, isOrdersFetching, fetchingUserId, users]);

  const handleViewCart = (user: User) => {
    setFetchingUserId(user.id);
  };

  return (
    <>
      <DashboardTableAccordion
        value="Show patients"
        title="Show Patients"
        accordionOpenValue={accordionOpenValue}
        onOpen={handleAccordionOpen}
        data={users}
        isLoading={isLoading}
        emptyMessage="No users found."
        loadingMessage="Loading users..."
        paginationMeta={meta}
        onPageChange={setPage}
        filtersComponent={
          <ShowPatientsFilters
            key={filtersResetKey}
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
          />
        }
        renderHeader={() => (
          <>
            <TableHead className="w-12"></TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="w-24"></TableHead>
          </>
        )}
        renderRow={(user, index) => (
          <>
            <TableCell>
              {(page - 1) * (meta?.per_page || 10) + index + 1}
            </TableCell>
            <TableCell>
              {user.name} {user.surname}
            </TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell className="text-right">
              <RelationActionButton
                onClick={() => handleViewCart(user)}
                disabled={fetchingUserId === user.id}
              >
                View
              </RelationActionButton>
            </TableCell>
          </>
        )}
      />

      <ModifyUserCart
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedUser={selectedUser}
      />
    </>
  );
}
